import type { RefObject } from "react";
import { useNotification } from "@context/NotificationContext";
import {
  drybulkCargoOperationsService,
  type CargoOperationDetail,
  type SequenceRequest,
  type SequenceView,
} from "@services/drybulkCargoOperationsApi";
import { useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
  SUBMITTABLE_STATUS,
} from "../constants";
import { problemDetail, problemStatus } from "../utils/problemDetail";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.draft`;

/** The Draft as it was when the page first loaded it: what Cancel restores. */
export interface VisitSnapshot {
  totalQuantity: number | null;
  sequences: SequenceView[];
}

export const snapshotOf = (detail: CargoOperationDetail): VisitSnapshot => ({
  totalQuantity: detail.totalQuantity,
  sequences: (detail.sequences ?? []).map((sequence) => ({ ...sequence })),
});

/** The API request that recreates a sequence; null when a required field is missing and it cannot be restored. */
const requestOf = (sequence: SequenceView): SequenceRequest | null =>
  sequence.plannedQuantity === null ||
  sequence.shipperCompanyCode === null ||
  sequence.stockPileCode === null ||
  sequence.cargoCode === null ||
  sequence.gradeSizeCode === null
    ? null
    : {
        plannedQuantity: sequence.plannedQuantity,
        hold: sequence.hold,
        shipperCompanyCode: sequence.shipperCompanyCode,
        stockPileCode: sequence.stockPileCode,
        cargoCode: sequence.cargoCode,
        gradeSizeCode: sequence.gradeSizeCode,
        actualSize: sequence.actualSize,
      };

const sameRequest = (a: SequenceRequest, b: SequenceRequest) =>
  a.plannedQuantity === b.plannedQuantity &&
  a.hold === b.hold &&
  a.shipperCompanyCode === b.shipperCompanyCode &&
  a.stockPileCode === b.stockPileCode &&
  a.cargoCode === b.cargoCode &&
  a.gradeSizeCode === b.gradeSizeCode &&
  a.actualSize === b.actualSize;

export interface UndoPlan {
  toRemove: SequenceView[];
  toRestore: { id: string; request: SequenceRequest }[];
  toAdd: SequenceRequest[];
  restoreTotal: number | null;
}

/** What it takes to bring `current` back to `snapshot`; the undo runs it and `hasVisitChanges` asks if it is empty. */
export const planUndo = (
  snapshot: VisitSnapshot,
  current: Pick<CargoOperationDetail, "totalQuantity" | "sequences">,
): UndoPlan => {
  const sequences = current.sequences ?? [];
  const before = new Map(snapshot.sequences.map((sequence) => [sequence.id, sequence]));
  const now = new Map(sequences.map((sequence) => [sequence.id, sequence]));
  return {
    toRemove: sequences.filter((sequence) => !before.has(sequence.id)),
    toRestore: sequences.flatMap((sequence) => {
      const original = before.get(sequence.id);
      const wanted = original ? requestOf(original) : null;
      const have = requestOf(sequence);
      return original && wanted && (!have || !sameRequest(have, wanted))
        ? [{ id: sequence.id, request: wanted }]
        : [];
    }),
    toAdd: snapshot.sequences
      .filter((sequence) => !now.has(sequence.id))
      .flatMap((sequence) => {
        const request = requestOf(sequence);
        return request ? [request] : [];
      }),
    restoreTotal:
      snapshot.totalQuantity !== null && current.totalQuantity !== snapshot.totalQuantity
        ? snapshot.totalQuantity
        : null,
  };
};

export const isEmptyPlan = (plan: UndoPlan) =>
  plan.toRemove.length === 0 &&
  plan.toRestore.length === 0 &&
  plan.toAdd.length === 0 &&
  plan.restoreTotal === null;

/** Whether the Draft differs from what this visit first loaded (a missing snapshot or detail means no). */
export const hasVisitChanges = (
  snapshot: VisitSnapshot | null,
  current: CargoOperationDetail | undefined,
) => Boolean(snapshot && current && !isEmptyPlan(planUndo(snapshot, current)));

/**
 * DBCO-0065 D18 - Cancel on a Draft reopened from the grid undoes everything saved during the visit. It reads the
 * operation fresh, and when it is still a Draft restores the snapshot with the existing calls in one business
 * transaction: remove the added sequences, put the edited ones back, add the removed ones again (they get new ids),
 * then restore the total. Not atomic: the first failure stops and shows the current state. Resolves true when the
 * page may be left (restored, nothing to restore, or the operation is gone).
 */
export const useUndoDraftVisit = (
  operationId: string,
  snapshotRef: RefObject<VisitSnapshot | null>,
) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const detailKey = DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId);

  return async (): Promise<boolean> => {
    const snapshot = snapshotRef.current;
    if (!snapshot) {
      return true;
    }
    let fresh: CargoOperationDetail;
    try {
      fresh = await drybulkCargoOperationsService.getDetail(operationId);
    } catch (error) {
      if (problemStatus(error) === 404) {
        return true;
      }
      notify({
        type: "error",
        title: t(`${L}.undoFailed`),
        description: problemDetail(error) ?? t("common.error"),
      });
      return false;
    }
    if (fresh.status !== SUBMITTABLE_STATUS) {
      notify({ type: "warning", title: t(`${DRYBULK_CARGO_OPS_LANG_BASE}.submit.conflict`) });
      void queryClient.invalidateQueries({ queryKey: detailKey });
      return false;
    }

    const plan = planUndo(snapshot, fresh);
    if (isEmptyPlan(plan)) {
      return true;
    }
    const { toRemove, toRestore, toAdd, restoreTotal } = plan;

    try {
      await runTransaction(
        {
          module: DRYBULK_CARGO_OPS_TELEMETRY.module,
          screen: DRYBULK_CARGO_OPS_TELEMETRY.operationScreen,
          action: "undo_draft_changes",
          entityType: "cargo_operation",
          entityId: operationId,
        },
        async (transactionId) => {
          const options = { transactionId };
          for (const sequence of toRemove) {
            await drybulkCargoOperationsService.removeSequence(operationId, sequence.id, options);
          }
          for (const { id, request } of toRestore) {
            await drybulkCargoOperationsService.updateSequence(operationId, id, request, options);
          }
          for (const request of toAdd) {
            await drybulkCargoOperationsService.addSequence(operationId, request, options);
          }
          if (restoreTotal !== null) {
            await drybulkCargoOperationsService.updateTotalQuantity(
              operationId,
              restoreTotal,
              options,
            );
          }
        },
      );
    } catch (error) {
      notify({
        type: "error",
        title: t(`${L}.undoFailed`),
        description: problemDetail(error) ?? t("common.error"),
      });
      void queryClient.invalidateQueries({ queryKey: detailKey });
      return false;
    }
    notify({ type: "success", title: t(`${L}.changesDiscarded`) });
    void queryClient.invalidateQueries({ queryKey: detailKey });
    void queryClient.invalidateQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.searchAll() });
    return true;
  };
};
